/**
 * External service marks on matches and ladder challenges (owner, 30 Oct
 * 2026, COMPLIANCE D193, revised contract: built for reuse, other integrations
 * for other sports are coming). Squash Levels is the only one today.
 *
 * The API sends, on fixtures and ladder challenges, `externalServices`: one
 * entry per integration that could ever take the result,
 *   {service: "squashLevels", name: "Squash Levels",
 *    status: "sending" | "notSending",
 *    reason?: "competition" | "you" | "player"}   (with notSending)
 * Absent or empty means no integration could take it, and then NOTHING is
 * shown. The API works out the reason, "you" included (the viewer plays in it
 * and is one of the players not sharing, by the export's own rule), so the
 * client uses it as sent and never reads the viewer's own setting.
 *
 * Shown ONLY on the match page and the ladder challenge page (owner, 30 Oct
 * 2026, mockup v7), as a small block per integration: its logo in a tile
 * (greyed when not sending), a bold "Sending to {name}" / "Not sending to
 * {name}" and the reason written out underneath, and for the viewer's own
 * setting a Turn on that opens the question in place, until a result is in. Never in lists or cards. No reason ever
 * names a person. The words live here and nowhere else, templated on the
 * entry's `name`, so a new integration needs only an API registry entry (and
 * a logo here, or it shows a neutral icon).
 *
 * Mirrors the app's external_service_marks.dart. Change both together. Pure:
 * imported explicitly (house style).
 */

const STATUSES = ['sending', 'notSending'];
const REASONS = ['competition', 'you', 'player'];

/** Marks by service key, bundled. An unknown key has none: the component draws a neutral icon with the name. */
const SERVICE_LOGOS = {
  squashLevels: {src: '/images/sl_icon.png'},
};

/**
 * Integrations a viewer who is not sharing can turn on IN PLACE, by the
 * account question the API serves for it (GET competitionQuestions/account/{key}).
 * Replaced "Turn on in your profile" (owner, 3 Nov 2026, SQUASH_LEVELS_CARDS_PLAN.md).
 * An integration not listed shows its words and no button.
 */
const TURN_ON_QUESTIONS = {
  squashLevels: 'squashLevels',
};
export const EXTERNAL_SERVICE_TURN_ON_LABEL = 'Turn on';

/**
 * Whether a match (a fixture, or a challenge's fixture) has a result entered.
 * Once it has, the "you" row is words only: no button, no switch, so nobody
 * turns sharing off because a match went badly.
 */
export function matchHasResult(fixture) {
  return fixture?.homeTeamResult != null && fixture?.awayTeamResult != null;
}

export function externalServiceLogo(service) {
  return (typeof service === 'string' && SERVICE_LOGOS[service]) || null;
}

const nonBlank = (v) => typeof v === 'string' && v.trim() !== '';

/** The entries the client can read, in the order the API sent them. */
export function externalServiceEntries(item) {
  const list = item?.externalServices;
  if (!Array.isArray(list)) return [];
  return list.filter((e) => e && nonBlank(e.service) && nonBlank(e.name) && STATUSES.includes(e.status));
}

/** The reason in the words people read. `kind` is "match" or "challenge". */
export function externalServiceReasonText(reasonKey, name, kind = 'match') {
  const noun = kind === 'challenge' ? 'challenge' : 'match';
  switch (reasonKey) {
    case 'sending':
      // Under the bold "Sending to {name}", so it says why, not where (mockup v7).
      return `Both players share their results, and this competition sends to ${name}.`;
    case 'you':
      return kind === 'challenge'
          ? `Any result from this challenge won't be sent to ${name}.`
          : `You aren't sharing with ${name}, so this match isn't sent.`;
    case 'competition':
      return `This competition doesn't send results to ${name}.`;
    case 'player':
      return `One or more players aren't sharing with ${name}.`;
    default:
      return `This ${noun} isn't sent to ${name}.`;
  }
}

/**
 * The section heading over the rows on the match and challenge pages (owner,
 * 3 Nov 2026): the integration's own name when there is one, else a general
 * word. Null when nothing is shown.
 */
export function externalServicesHeading(item) {
  const list = externalServiceEntries(item);
  if (!list.length) return null;
  return list.length === 1 ? list[0].name : 'External services';
}

/**
 * What one entry's mark shows. `hasResult`: the match already has a result,
 * so the "you" row offers nothing to press.
 */
export function externalServiceMark(entry, kind = 'match', {hasResult = false} = {}) {
  const sending = entry.status === 'sending';
  const reasonKey = sending ? 'sending' : (REASONS.includes(entry.reason) ? entry.reason : 'unknown');
  return {
    service: entry.service,
    name: entry.name,
    logo: externalServiceLogo(entry.service),
    sending,
    reasonKey,
    label: `${sending ? 'Sending' : 'Not sending'} to ${entry.name}`,
    reason: externalServiceReasonText(reasonKey, entry.name, kind),
    turnOn: reasonKey === 'you' && !hasResult && TURN_ON_QUESTIONS[entry.service]
        ? {label: EXTERNAL_SERVICE_TURN_ON_LABEL, questionKey: TURN_ON_QUESTIONS[entry.service]}
        : null,
  };
}


/** The match and challenge pages: every integration, side by side, in the order the API sent them. */
export function externalServiceDetailMarks(item, kind = 'match', options = {}) {
  return externalServiceEntries(item).map((e) => externalServiceMark(e, kind, options));
}

/**
 * The query for GET ladderChallenges/externalServicesPreview (the Send
 * challenge form, before the challenge exists), or null until the ladder and
 * both teams are known. The API answers with the same `externalServices` list
 * the challenge will carry once sent, so it is drawn with the same rows and
 * the "challenge" wording.
 */
export function externalServicesPreviewQuery(ladderId, challengingTeamId, challengedTeamId) {
  if (![ladderId, challengingTeamId, challengedTeamId].every(nonBlank)) return null;
  if (challengingTeamId === challengedTeamId) return null;
  return {ladderId, challengingTeamId, challengedTeamId};
}

/** The preview's answer as something the rows read, or null on garbage (a failed call shows nothing). */
export function externalServicesPreviewItem(response) {
  const list = externalServiceEntries(response);
  return list.length ? {externalServices: list} : null;
}
